import { useEffect, useState } from "react";
import {
  addOrUpdateCustomer,
  deleteCustomer,
} from "../../../../services/api/customers/CustomersService";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { ApiException } from "../../../../services/api/ApiException";
import { toast } from "react-toastify";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { ICustomer } from "../../../../@types/customer.type";
import { useNavigate } from "react-router-dom";
import vectorIcon from "../../../../assets/_VectorIcon_.svg";
import { useConfirm } from "../../../../hooks/useConfirm";
import usePermission from "../../../../hooks/usePermission";
import { FeatureStatic } from "../../../../@types/statics/feature.static";
import { ICustomField } from "../../../../@types/customField.type";
import { mapFieldTypeOptionsValue } from "../../../../utils/mapFieldTypeOptions";
import { getAllCustomersCustomField } from "../../../../services/api/customerCustomField/customerCustomFieldService";
import { validateCustomFields } from "../../../../utils/validateCustomFields";
import { includeCustomFieldstoApi } from "../../adapters/customersCustomFields";
import { OptionsDynimicFields } from "../../../../components/OptionsDynimicFields";
import styles from "./styles.module.scss";

type FormData = {
  name: string;
  description?: string;
  originId?: string;
};

const schema = z.object({
  name: z
    .string()
    .min(1, "Nome é obrigatório")
    .max(100, "Nome deve ter no máximo 100 caracteres"),
  description: z.string().optional(),
  originId: z
    .string()
    .max(36, "Id Origem deve ter no máximo 36 caracteres")
    .optional(),
});

interface FormCustomersProps {
  pageType: "create" | "update" | "seeMore";
  customerData?: ICustomer;
}

export const FormCustomers: React.FC<FormCustomersProps> = ({
  pageType,
  customerData,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [customersCustomField, setCustomersCustomField] = useState<
    ICustomField[]
  >([]);
  const [formDataCustomFields, setFormDataCustomFields] = useState(
    {} as Record<string, any>
  );
  const [errorsFormDataCustomFields, setErrorsFormDataCustomFields] = useState(
    {}
  );
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: customerData?.name || "",
      description: customerData?.description || "",
      originId: customerData?.originId || "",
    },
  });

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);

  const { showConfirm, Confirm } = useConfirm(
    "Tem certeza que deseja excluir o cliente?",
    openModalConfirm,
    handleCloseModalConfirm
  );

  const navigate = useNavigate();
  const permissions = usePermission(FeatureStatic.clients.id);

  useEffect(() => {
    const loadCustomersCustomFields = async () => {
      try {
        const responseCustomerCustomField = await getAllCustomersCustomField();
        setCustomersCustomField(responseCustomerCustomField.result.items);
      } catch (error) {
        console.log(error);
      }
    };

    loadCustomersCustomFields();
  }, []);

  useEffect(() => {
    if (
      customersCustomField.length > 0 &&
      customerData?.customFieldsObject !== undefined
    ) {
      const customFieldsInitialState = customersCustomField.reduce(
        (acc, field) => {
          let initialValue;
          switch (mapFieldTypeOptionsValue[field.customFieldTypeId]) {
            case "booleano":
              initialValue = "Não";
              break;
            case "data":
              initialValue = "";
              break;
            case "lista":
              initialValue = "";
              break;
            case "textoMultlinhas":
            case "texto":
            default:
              initialValue = "";
          }

          acc[field.name] = customerData.customFieldsObject?.[field.name]
            ? customerData.customFieldsObject[field.name]
            : initialValue;

          return acc;
        },
        {} as Record<string, any>
      );

      setFormDataCustomFields(() => ({
        ...customFieldsInitialState,
      }));
    }
  }, [customersCustomField, customerData]);

  const onSubmit = async (data: FormData) => {
    const customerToApi = {
      id: customerData?.id,
      name: data.name,
      description: data.description,
      originId: data.originId,
    };

    setErrorsFormDataCustomFields({});
    const newErrors: any = {};
    customersCustomField.forEach((field) => {
      const error = validateCustomFields(
        field,
        formDataCustomFields[field.name]
      );

      if (error) {
        newErrors[field?.name] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrorsFormDataCustomFields(newErrors);
      return;
    }

    const customerWithCustomFields = includeCustomFieldstoApi(
      customerToApi,
      formDataCustomFields
    );

    try {
      setLoading(true);
      const response = await addOrUpdateCustomer(customerWithCustomFields);
      if (response?.statusCode === 200) {
        reset();
        toast.success("Cliente salvo com sucesso!");
        navigate("/customers");
      }
    } catch (error: any) {
      toast.error("Erro ao salvar o Cliente");
      new ApiException(error.message || "Erro ao processar a requisição.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    handleOpenModalConfirm();
    const isConfirm = await showConfirm();

    if (isConfirm) {
      await deleteCustomer(customerData?.id as string);
      toast.success("Cliente excluído com sucesso!");
      navigate("/customers");
    }
  };

  return (
    <>
      <div className={styles.back}>
        <Button
          className={styles.button}
          onClick={() => navigate("/customers")}
        >
          {vectorIcon && <img src={vectorIcon} />}
          Voltar a tela anterior
        </Button>
      </div>
      <div className={styles.title}>
        <h1>
          {pageType === "update" ? `${customerData?.name}` : "Novo Cliente"}
        </h1>
      </div>
      <div></div>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.formCustomer}>
        <div className={styles.fields}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                label="Nome"
                disabled={!permissions.canEdit()}
                value={field.value}
                placeholder={
                  pageType === "create"
                    ? "Insira o nome do cliente aqui..."
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={field.onChange}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
        </div>
        <div className={styles.fields}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                label="Descrição"
                disabled={!permissions.canEdit()}
                multiline
                rows={3}
                value={field.value}
                placeholder={
                  pageType === "create"
                    ? "Insira a descrição do cliente aqui..."
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={field.onChange}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />
        </div>
        <div className={styles.fields}>
          <Controller
            name="originId"
            control={control}
            render={({ field }) => (
              <TextField
                label="Id Origem"
                disabled={!permissions.canEdit()}
                value={field.value}
                placeholder={
                  pageType === "create"
                    ? "Insira o Id Origem do cliente aqui..."
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={field.onChange}
                error={!!errors.originId}
                helperText={errors.originId?.message}
              />
            )}
          />
        </div>

        <OptionsDynimicFields
          dynamicFields={customersCustomField}
          formDataCustomFields={formDataCustomFields}
          setFormDataCustomFields={setFormDataCustomFields}
          errorsFormDataCustomFields={errorsFormDataCustomFields}
        />

        <div className={styles.btnContainer}>
          {customerData?.id && permissions.canEdit() && (
            <Button
              variant="contained"
              color="secondary"
              type="button"
              onClick={handleDeleteClick}
              className={styles.btnsDeleted}
              disabled={loading}
            >
              Excluir Cliente
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className={styles.btnsSave}
            disabled={loading}
          >
            Salvar
          </Button>
        </div>
      </form>
      {Confirm}
    </>
  );
};
